import { useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Shuffle } from "lucide-react";

export function ReassignDialog({ open, onOpenChange, users = [], onDone, presetOwnerId = "" }) {
  const { t, picklists } = useApp();
  const [plants, setPlants] = useState([]);
  const [filters, setFilters] = useState({ segment: "", plant_id: "", current_owner_id: presetOwnerId });
  const [preview, setPreview] = useState(null);
  const [newOwner, setNewOwner] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFilters({ segment: "", plant_id: "", current_owner_id: presetOwnerId });
    setPreview(null); setNewOwner("");
    api.get("/iam/plants?page_size=200").then((r) => setPlants(r.data.items || [])).catch(() => {});
  }, [open, presetOwnerId]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.post("/iam/reassign/preview", body);
      setPreview(data);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/iam/reassign/execute", {
        account_ids: preview.accounts.map((a) => a.id), new_owner_id: newOwner, notify,
      });
      toast.success(`${data.updated} ${t("iam_affected_accounts")}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shuffle className="h-5 w-5 text-[#E8871E]" />{t("iam_reassign")}</DialogTitle>
          <DialogDescription>{t("iam_reassign_help")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-[11px]">{t("segment")}</Label>
              <Select value={filters.segment || "any"} onValueChange={(v) => setFilters((f) => ({ ...f, segment: v === "any" ? "" : v }))}>
                <SelectTrigger data-testid="reassign-segment-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">–</SelectItem>
                  {(picklists?.segments || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-[11px]">{t("iam_plants")}</Label>
              <Select value={filters.plant_id || "any"} onValueChange={(v) => setFilters((f) => ({ ...f, plant_id: v === "any" ? "" : v }))}>
                <SelectTrigger data-testid="reassign-plant-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">–</SelectItem>
                  {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-[11px]">{t("iam_current_owner")}</Label>
              <Select value={filters.current_owner_id || "any"} onValueChange={(v) => setFilters((f) => ({ ...f, current_owner_id: v === "any" ? "" : v }))}>
                <SelectTrigger data-testid="reassign-owner-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">–</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="reassign-preview-btn">
            {t("iam_reassign_preview")}
          </Button>

          {preview && (
            <div className="space-y-3" data-testid="reassign-preview-result">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-[#1B2A4A] text-white">{preview.account_count} {t("iam_affected_accounts")}</Badge>
                <Badge variant="outline">{preview.open_quotes} {t("iam_open_quotes")}</Badge>
                <Badge variant="outline">{preview.open_opportunities} {t("iam_open_opps")}</Badge>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-gray-100 divide-y divide-gray-50">
                {preview.accounts.map((a) => (
                  <div key={a.id} className="px-3 py-1.5 text-xs flex gap-2" data-testid={`reassign-acc-${a.id}`}>
                    <span className="font-medium">{a.name}</span>
                    <span className="text-gray-400">{a.primary_segment}</span>
                    <span className="ml-auto text-gray-500">{a.owner_name || "–"}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="space-y-1 flex-1 min-w-[200px]"><Label className="text-[11px]">{t("iam_new_owner")}</Label>
                  <Select value={newOwner} onValueChange={setNewOwner}>
                    <SelectTrigger data-testid="reassign-new-owner-select" className="h-9"><SelectValue placeholder="–" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <label className="flex items-center gap-2 text-xs h-9">
                  <Switch checked={notify} onCheckedChange={setNotify} data-testid="reassign-notify-switch" />{t("iam_notify_owners")}
                </label>
                <Button className="bg-[#E8871E] hover:bg-[#d1751a] text-white" disabled={!newOwner || busy}
                  onClick={execute} data-testid="reassign-execute-btn">{t("iam_execute")}</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
